
import { useState, useEffect, useCallback } from 'react';
import { notificationService } from '@/services/notificationService';
import { Notification, NotificationType } from '@/types/notification';
import { useAuth } from '@/contexts/AuthContext'; // Updated import path

export const useNotifications = () => {
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const { user } = useAuth();

  const fetchNotifications = useCallback(async () => {
    if (!user) return;

    setLoading(true);
    try {
      // For development mode with mock user, handle separately
      let fetchedNotifications: Notification[] = [];
      
      if (user.id === '12345-dev-user') {
        // Use mock notifications for development
        fetchedNotifications = getMockNotifications();
      } else {
        // Real API call for production
        const result = await notificationService.getNotifications(user.id);
        fetchedNotifications = result || [];
      }
      
      setNotifications(fetchedNotifications);
      setUnreadCount(fetchedNotifications.filter(n => !n.isRead).length);
    } catch (error) {
      console.error('Error fetching notifications:', error);
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    fetchNotifications();
  }, [fetchNotifications]);

  const markAsRead = useCallback(async (notificationId: string) => {
    const success = await notificationService.markAsRead(notificationId);
    if (success) {
      await fetchNotifications();
    }
    return success;
  }, [fetchNotifications]);

  const markAllAsRead = useCallback(async () => {
    if (!user) return;
    
    // For development mode with mock user
    if (user.id === '12345-dev-user') {
      setNotifications(prevNotifications => 
        prevNotifications.map(n => ({ ...n, isRead: true }))
      );
      setUnreadCount(0);
      return;
    }
    
    const success = await notificationService.markAllAsRead(user.id);
    if (success) {
      await fetchNotifications();
    }
    return success;
  }, [user, fetchNotifications]);

  const createNotification = useCallback(async (
    notification: {
      title: string, 
      message: string, 
      type: NotificationType, 
      link?: string
    }
  ) => {
    if (!user) return null;
    
    // For development mode with mock user
    if (user.id === '12345-dev-user') {
      const mockNotification: Notification = {
        id: `mock-${Date.now()}`,
        ...notification,
        description: notification.message,
        isRead: false,
        date: new Date().toISOString(),
      };
      setNotifications(prev => [mockNotification, ...prev]);
      setUnreadCount(prev => prev + 1);
      return mockNotification;
    }
    
    const newNotification = await notificationService.createNotification(user.id, notification);
    if (newNotification) {
      await fetchNotifications();
    }
    return newNotification;
  }, [user, fetchNotifications]);

  // Function to get recent notifications for the notification bell
  const getRecentNotifications = useCallback((limit: number = 5) => {
    return notifications.slice(0, limit);
  }, [notifications]);

  return {
    notifications,
    unreadCount,
    loading,
    fetchNotifications,
    markAsRead,
    markAllAsRead,
    createNotification,
    getRecentNotifications
  };
};

// Helper function to generate mock notifications for development
const getMockNotifications = (): Notification[] => {
  return [
    {
      id: 'mock-1',
      type: 'message',
      title: 'New Message',
      message: 'You have a new message from Dr. Smith',
      description: 'You have a new message from Dr. Smith',
      isRead: false,
      date: new Date().toISOString(),
      link: '/messages/1'
    },
    {
      id: 'mock-2',
      type: 'appointment',
      title: 'Appointment Reminder',
      message: 'Your appointment is scheduled for tomorrow at 2:00 PM',
      description: 'Your appointment is scheduled for tomorrow at 2:00 PM',
      isRead: true,
      date: new Date().toISOString(),
      link: '/appointments/2'
    },
    {
      id: 'mock-3',
      type: 'system',
      title: 'System Update',
      message: 'The system will be undergoing maintenance tonight',
      description: 'The system will be undergoing maintenance tonight',
      isRead: false,
      date: new Date().toISOString()
    }
  ];
};
